import React, { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import RootLoader from '../../components/root-loader/index';
import {
  SubscriptionOfferStatus,
  useSubscriptionOffer,
} from '../../hooks/subscription-offer/use-subscription-offer';
import { accessTokenStorage } from '../../storage/auth/access-token-storage.util';
import { subscriptionOfferStore } from '../../storage/local/index';
import { PageRoute } from '../../types/page.types';

export const SubscriptionOffer: FC = () => {
  // Reached via /{planType}-offer/:slug or the /billing/:id activation link — same slug either way.
  const { slug: slugParam, id } = useParams<{ id?: string; slug?: string }>();
  const slug = slugParam ?? id;
  const navigate = useNavigate();
  const intl = useIntl();
  const { data: offer, isLoading, isError } = useSubscriptionOffer(slug);

  useEffect(() => {
    if (isError) {
      toast.error(intl.formatMessage({ id: 'OFFER_LINK_INVALID' }));
      navigate(PageRoute.LOGIN, { replace: true });
      return;
    }

    if (!offer || !slug) return;

    const isAuthenticated = !!accessTokenStorage.getState().get();

    if (offer.status === SubscriptionOfferStatus.ACTIVE) {
      subscriptionOfferStore.delete();
      toast.info(intl.formatMessage({ id: 'OFFER_ALREADY_ACTIVATED' }));
      navigate(isAuthenticated ? PageRoute.MY_COMPANIES : PageRoute.LOGIN, { replace: true });
      return;
    }

    // Persist the offer so it survives the sign-up / sign-in round-trip (incl. Google OAuth, which
    // redirects to the app root). The AuthGuard picks it up post-authentication and runs the claim.
    subscriptionOfferStore.set({ slug, planType: offer.planType });

    // AuthGuard on the home route runs the claim for authenticated users and bounces guests to
    // /login, where the offer store persists until they finish authenticating.
    navigate(PageRoute.HOME, { replace: true });
  }, [offer, isError, slug, navigate, intl]);

  if (isLoading || (!offer && !isError)) return <RootLoader />;

  return <RootLoader />;
};
